import moment from 'moment';
import router from '@/router';
import {
  clearStoredCompanyPermissions,
  loadUserCompanyPermissions,
  persistCompanyPermissions,
  readStoredCompanyPermissions,
} from '@/services/portalPermissions';
import { ROUTE_MODULE_PERMISSION } from '@/constants/portalPermissions';
import {
  canAccessRoute as checkRouteAccess,
  filterCustomerCompaniesByModule,
  getPermittedCompanyIds,
  hasAnyCompanyModulePermission,
} from '@/utils/portalPermissionAccess';

const store = {
  namespaced: true,
  state: {
    user: {},
    companyPermissions: readStoredCompanyPermissions(),
  },
  getters: {
    adminCompanyIds(state) {
      return Object.keys(state.companyPermissions)
        .filter((customerId) => state.companyPermissions[customerId]?.portalAdmin)
        .map((customerId) => Number(customerId));
    },
    hasAnyPortalAdmin(_state, getters) {
      return getters.adminCompanyIds.length > 0;
    },
    isPortalAdmin: (state) => (customerId) => Boolean(
      state.companyPermissions[String(customerId)]?.portalAdmin,
    ),
    hasModulePermission: (state) => (code) => hasAnyCompanyModulePermission(
      state.companyPermissions,
      code,
    ),
    canAccessRoute: (state) => (routeName) => checkRouteAccess(state.companyPermissions, routeName),
    permittedCompanyIdsForModule: (state) => (moduleCode) => getPermittedCompanyIds(
      state.companyPermissions,
      moduleCode,
    ),
    permittedCustomerCompaniesForModule: (state) => (moduleCode) => filterCustomerCompaniesByModule(
      state.user?.companies,
      state.companyPermissions,
      moduleCode,
    ),
    permittedCustomerCompaniesForRoute: (state) => (routeName) => {
      const moduleCode = ROUTE_MODULE_PERMISSION[routeName];

      return filterCustomerCompaniesByModule(
        state.user?.companies,
        state.companyPermissions,
        moduleCode,
      );
    },
  },
  mutations: {
    UPDATE_USER(state, payload) {
      // eslint-disable-next-line no-param-reassign
      payload.hiringDate = moment(payload.hiringDate).format('YYYY-MM-DD');
      state.user = payload.person;

      localStorage.removeItem('@GSCMX-PC:user');
      if (payload?.person) {
        const userObject = {
          id: payload.person.id,
          name: payload.person.name,
          email: payload.person.email,
          photo: payload.person.photo,
        };
        localStorage.setItem('@GSCMX-PC:user', JSON.stringify(userObject));
      }

      localStorage.removeItem('@GSCMX-PC:companies');
      if (payload?.person?.companies) {
        const filteredCompanies = payload.person.companies
          .filter((company) => company.customer && !company.customBroker)
          .map((company) => ({ id: company.id, name: company.name.toUpperCase() }));
        localStorage.setItem('@GSCMX-PC:companies', JSON.stringify(filteredCompanies));
      }
    },
    SET_COMPANY_PERMISSIONS(state, companyPermissions) {
      state.companyPermissions = companyPermissions;
      persistCompanyPermissions(companyPermissions);
    },
  },
  actions: {
    updateUser(context, payload) {
      context.commit('UPDATE_USER', payload);
    },
    async loadCompanyPermissions({ commit, state }) {
      const userId = state.user?.id;
      const companies = state.user?.companies;

      if (!userId || !companies) {
        commit('SET_COMPANY_PERMISSIONS', {});
        return;
      }

      try {
        const companyPermissions = await loadUserCompanyPermissions(userId, companies);
        commit('SET_COMPANY_PERMISSIONS', companyPermissions);
      } catch {
        commit('SET_COMPANY_PERMISSIONS', {});
      }
    },
    logout(context) {
      localStorage.removeItem('@GSCMX-PC:access_token');
      localStorage.removeItem('@GSCMX-PC:refresh_token');
      localStorage.removeItem('@GSCMX-PC:bi_token');
      localStorage.removeItem('@GSCMX-PC:user');
      localStorage.removeItem('@GSCMX-PC:companies');
      clearStoredCompanyPermissions();
      context.commit('UPDATE_USER', {});
      context.commit('SET_COMPANY_PERMISSIONS', {});
      router.push('/login');
    },
  },
};

export default store;
